import React from "react";
import { useTranslation } from "react-i18next";

const toNumber = (value) => {
  if (value === "" || value == null) return null;
  const n = Number(String(value).replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const asString = (value) => (value == null ? "" : String(value));

const normalizeDateForInput = (value) => {
  const raw = asString(value).trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);

  const fr = raw.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (fr) {
    const [, d, m, y] = fr;
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }

  return raw;
};

const normalizeComparableText = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[’']/g, "'")
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeAcquisitionType = (value) => {
  const raw = normalizeComparableText(value);
  if (!raw) return "";

  const aliases = {
    purchase: [
      "purchased",
      "purchase",
      "achat",
      "achetee",
      "achete",
      "acquired",
      "acquisition",
      "buy",
    ],
    artist: [
      "artist",
      "artiste",
      "direct artist",
      "artist direct",
      "artist sale",
      "vente artiste",
      "vente directe artiste",
      "atelier",
      "studio",
    ],
    gallery: [
      "gallery",
      "galerie",
      "dealer",
      "marchand",
    ],
    auction: [
      "auction",
      "auction house",
      "maison de vente",
      "maison de vente encheres",
      "maison de vente aux encheres",
      "enchere",
      "encheres",
      "vente aux encheres",
      "vente enchere",
      "bordereau",
      "adjudication",
      "tajan",
      "artcurial",
      "christies",
      "sothebys",
    ],
    private: [
      "private",
      "particulier",
      "private sale",
      "vente privee",
    ],
    donation: [
      "donated",
      "donation",
      "don",
      "gift",
      "schenkung",
    ],
    inheritance: [
      "inheritance",
      "heritage",
      "heritage succession",
      "succession",
      "legs",
    ],
    other: [
      "other",
      "autre",
    ],
  };

  for (const [canonical, values] of Object.entries(aliases)) {
    if (values.some((item) => raw === item || raw.includes(item))) return canonical;
  }

  return raw;
};

const clearAuctionFields = (handleInputChange) => {
  [
    "auction_house",
    "auction_sale_name",
    "auction_sale_date",
    "auction_lot_number",
    "auction_hammer_price",
    "auction_fees",
    "acquisitionDetails.auction_house",
    "acquisitionDetails.auction_sale_name",
    "acquisitionDetails.auction_sale_date",
    "acquisitionDetails.auction_lot_number",
    "acquisitionDetails.lot_number",
    "acquisitionDetails.auction_hammer_price",
    "acquisitionDetails.hammer_price",
    "acquisitionDetails.auction_fees",
  ].forEach((name) => {
    handleInputChange({ target: { name, value: "" } });
  });
};

const AcquisitionSection = ({ formData = {}, handleInputChange, readOnly = false, embedded = false }) => {
  const { t } = useTranslation();
  const A = "artworkEdit.acquisitionForm";
  if (!formData || Object.keys(formData).length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-gray-500"></div>
        <p className="mt-4 text-gray-600">{t(`${A}.loading`)}</p>
      </div>
    );
  }

  const acq = formData.acquisitionDetails || {};

  const hasAuctionData = Boolean(
    formData.auction_house ||
    formData.auction_lot_number ||
    formData.auction_hammer_price ||
    formData.auction_fees ||
    acq.auction_house ||
    acq.lot_number ||
    acq.auction_lot_number ||
    acq.hammer_price ||
    acq.auction_hammer_price ||
    acq.auction_fees
  );

  const acquisitionTypeRaw =
    formData.acquisition_type ||
    acq.acquisition_type ||
    acq.type ||
    acq.sale_type ||
    (hasAuctionData ? "auction" : "");

  const acquisitionType = normalizeAcquisitionType(acquisitionTypeRaw);

  const acquisitionDate =
    formData.acquisition_date || acq.date || acq.acquisition_date || "";

  // Source = provenance d’achat générique. Elle ne doit jamais se remplir
  // automatiquement depuis auction_house, sinon un achat artiste devient
  // visuellement une maison de vente.
  const acquisitionSource =
    formData.acquisition_source || acq.source || acq.acquisition_source || "";

  const acquisitionPrice =
    formData.acquisition_price ??
    acq.acquisition_price ??
    acq.purchase_price ??
    acq.price ??
    acq.lot_total ??
    "";

  const auctionHouse = formData.auction_house || acq.auction_house || (acquisitionType === "auction" ? formData.acquisition_source || acq.acquisition_source || acq.source || "" : "");
  const auctionSaleName = formData.auction_sale_name || acq.auction_sale_name || "";
  const auctionSaleDate = formData.auction_sale_date || acq.auction_sale_date || "";
  const auctionLotNumber =
    formData.auction_lot_number || acq.auction_lot_number || acq.lot_number || "";
  const auctionHammerPrice =
    formData.auction_hammer_price ?? acq.auction_hammer_price ?? acq.hammer_price ?? "";
  const auctionFees = formData.auction_fees ?? acq.auction_fees ?? "";

  // Devise d’acquisition uniquement.
  // Ne pas utiliser formData.currency ici : currency = devise vente / galerie.
  const acquisitionCurrency =
    formData.acquisition_currency ||
    acq.acquisition_currency ||
    "EUR";
  const isNonEur = acquisitionCurrency !== "EUR";
  const exchangeRate = formData.acquisition_exchange_rate ?? acq.acquisition_exchange_rate ?? "";
  const priceEur = formData.acquisition_price_eur ?? acq.acquisition_price_eur ?? "";

  const isAuction = acquisitionType === "auction";
  const showSourceField = acquisitionType !== "auction";
  const showPriceBlock = [
    "purchase",
    "artist",
    "gallery",
    "private",
    "auction",
    "donation",
    "exchange",
    "inheritance",
    "other",
  ].includes(acquisitionType);

  const setAcquisitionType = (nextType) => {
    handleInputChange({ target: { name: "acquisitionDetails.type", value: nextType } });
    handleInputChange({ target: { name: "acquisitionDetails.acquisition_type", value: nextType } });
    handleInputChange({ target: { name: "acquisition_type", value: nextType } });

    if (nextType !== "auction") clearAuctionFields(handleInputChange);
  };

  // Recalcule acquisition_price_eur à chaque changement de prix ou de taux.
  // Appelé par les onChange du prix total ET du taux de change.
  const syncEurConversion = (nextPrice, nextRate) => {
    const p = parseFloat(String(nextPrice).replace(",", ".")) || 0;
    const r = parseFloat(String(nextRate).replace(",", ".")) || 0;
    const eur = (p > 0 && r > 0) ? String(Math.round(p * r * 100) / 100) : "";
    handleInputChange({ target: { name: "acquisition_price_eur",    value: eur } });
    handleInputChange({ target: { name: "acquisition_exchange_rate", value: nextRate } });
    handleInputChange({ target: { name: "acquisitionDetails.acquisition_price_eur",    value: eur } });
    handleInputChange({ target: { name: "acquisitionDetails.acquisition_exchange_rate", value: nextRate } });
  };

  const syncTotalFromAuction = (nextHammer, nextFees) => {
    const hammer = toNumber(nextHammer) || 0;
    const fees = toNumber(nextFees) || 0;
    if (hammer > 0 || fees > 0) {
      const total = String(hammer + fees);
      handleInputChange({ target: { name: "acquisitionDetails.price", value: total } });
      handleInputChange({ target: { name: "acquisitionDetails.lot_total", value: total } });
      handleInputChange({ target: { name: "acquisitionDetails.purchase_price", value: total } });
      handleInputChange({ target: { name: "acquisition_price", value: total } });
      // Ne plus écrire dans price : champ legacy ambigu.
      // Recalcule la conversion EUR si devise étrangère
      if (isNonEur) syncEurConversion(total, exchangeRate);
    }
  };

  const disabled = !!readOnly;

  return (
    <div className={embedded ? "" : "border-b border-gray-medium pb-8"}>
      {!embedded && (
        <div className="mb-6">
          <h2 className="text-sm font-medium uppercase tracking-wider text-gray-dark">
            {t(`${A}.heading`)}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t(`${A}.headingHelp`)}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="block text-sm mb-2">{t(`${A}.type`)}</label>
          <select
            name="acquisition_type"
            value={acquisitionType}
            onChange={(e) => setAcquisitionType(e.target.value)}
            disabled={disabled}
            className="form-control-artsy w-full"
          >
            <option value="">{t(`${A}.selectType`)}</option>
            <option value="artist">{t(`${A}.typeArtist`)}</option>
            <option value="gallery">{t(`${A}.typeGallery`)}</option>
            <option value="auction">{t(`${A}.typeAuction`)}</option>
            <option value="private">{t(`${A}.typePrivate`)}</option>
            <option value="donation">{t(`${A}.typeDonation`)}</option>
            <option value="exchange">{t(`${A}.typeExchange`)}</option>
            <option value="inheritance">{t(`${A}.typeInheritance`)}</option>
            <option value="purchase">{t(`${A}.typePurchase`)}</option>
            <option value="other">{t(`${A}.typeOther`)}</option>
          </select>
        </div>

        <div>
          <label className="block text-sm mb-2">{t(`${A}.date`)}</label>
          <input
            type="date"
            name="acquisitionDetails.date"
            value={normalizeDateForInput(acquisitionDate)}
            onChange={(e) => {
              handleInputChange(e);
              handleInputChange({ target: { name: "acquisition_date", value: e.target.value } });
            }}
            disabled={disabled}
            className="form-control-artsy w-full"
          />
        </div>
      </div>

      {showSourceField && (
        <div className="mb-6">
          <label className="block text-sm mb-2">
            {t(`${A}.sourceLabel.${acquisitionType}`, { defaultValue: t(`${A}.sourceGeneric`) })}
          </label>
          <input
            type="text"
            name="acquisitionDetails.source"
            value={acquisitionSource}
            onChange={(e) => {
              const value = e.target.value;

              handleInputChange(e);
              handleInputChange({ target: { name: "acquisition_source", value } });
              handleInputChange({ target: { name: "acquisitionDetails.acquisition_source", value } });

              if (acquisitionType === "private") {
                handleInputChange({ target: { name: "acquisitionDetails.seller", value } });
                handleInputChange({ target: { name: "acquisitionDetails.private_seller", value } });
              }

              if (acquisitionType === "artist") {
                handleInputChange({ target: { name: "acquisitionDetails.seller", value } });
                handleInputChange({ target: { name: "acquisitionDetails.artist_seller", value } });
              }

              if (acquisitionType === "gallery") {
                handleInputChange({ target: { name: "acquisitionDetails.gallery", value } });
              }

              if (acquisitionType === "donation") {
                handleInputChange({ target: { name: "acquisitionDetails.donor", value } });
              }

	      if (acquisitionType === "exchange") {
	         handleInputChange({ target: { name: "acquisitionDetails.exchange_counterparty", value } });
	         handleInputChange({ target: { name: "acquisitionDetails.counterparty", value } });
              }
            }}
            disabled={disabled}
            className="form-control-artsy w-full"
            placeholder={t(`${A}.sourcePlaceholder`)}
          />
        </div>
      )}

      {showPriceBlock && (
        <div className="space-y-5">
          {/* Devise d’acquisition — distincte de currency, réservé vente / galerie */}
          <div>
            <label className="block text-sm mb-2">{t(`${A}.purchaseCurrency`)}</label>
            <select
              name="acquisition_currency"
              value={acquisitionCurrency}
              onChange={(e) => {
                const value = e.target.value;
                handleInputChange(e);
                handleInputChange({ target: { name: "acquisitionDetails.acquisition_currency", value } });

                if (value === "EUR") {
                  handleInputChange({ target: { name: "acquisition_price_eur", value: "" } });
                  handleInputChange({ target: { name: "acquisition_exchange_rate", value: "" } });
                  handleInputChange({ target: { name: "acquisitionDetails.acquisition_price_eur", value: "" } });
                  handleInputChange({ target: { name: "acquisitionDetails.acquisition_exchange_rate", value: "" } });
                } else if (acquisitionPrice && exchangeRate) {
                  syncEurConversion(acquisitionPrice, exchangeRate);
                }
              }}
              disabled={disabled}
              className="form-control-artsy w-full md:w-48"
            >
              <option value="EUR">EUR — {t(`${A}.currencyEuro`)}</option>
              <option value="USD">USD — {t(`${A}.currencyUsd`)}</option>
              <option value="GBP">GBP — {t(`${A}.currencyGbp`)}</option>
              <option value="CHF">CHF — {t(`${A}.currencyChf`)}</option>
              <option value="HKD">HKD — {t(`${A}.currencyHkd`)}</option>
              <option value="JPY">JPY — {t(`${A}.currencyJpy`)}</option>
              <option value="CNY">CNY — {t(`${A}.currencyCny`)}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm mb-2">{t(`${A}.price`)}</label>
            <input
              type="number"
              name="acquisitionDetails.price"
              value={acquisitionPrice || ""}
              onChange={(e) => {
                handleInputChange(e);
                handleInputChange({ target: { name: "acquisition_price", value: e.target.value } });
                handleInputChange({ target: { name: "acquisitionDetails.acquisition_price", value: e.target.value } });
                handleInputChange({ target: { name: "acquisitionDetails.purchase_price", value: e.target.value } });
                // Ne plus écrire dans price : champ legacy ambigu.
                handleInputChange({ target: { name: "acquisitionDetails.exchange_value", value: e.target.value } });
                handleInputChange({ target: { name: "acquisitionDetails.declared_value", value: e.target.value } });
                if (isNonEur) syncEurConversion(e.target.value, exchangeRate);
              }}
              disabled={disabled}
              className="form-control-artsy w-full"
              min="0"
              placeholder={t(`${A}.pricePlaceholder`)}
            />
          </div>

          {/* Conversion EUR — uniquement si la devise n'est pas EUR */}
          {isNonEur && (
            <div className="rounded-lg border border-[#C9A961]/40 bg-[#F9F7F4] p-4 space-y-4">
              <p className="text-xs uppercase tracking-[0.14em] text-[#C9A961] font-medium" style={{ fontFamily: "Georgia, serif" }}>
                {t(`${A}.eurConversion`)}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-2">
                    {t(`${A}.exchangeRate`)}
                    <span className="ml-1 text-gray-400 font-normal">{t(`${A}.exchangeRateSuffix`, { currency: acquisitionCurrency })}</span>
                  </label>
                  <input
                    type="number"
                    name="acquisition_exchange_rate"
                    value={exchangeRate}
                    onChange={(e) => syncEurConversion(acquisitionPrice, e.target.value)}
                    disabled={disabled}
                    className="form-control-artsy w-full"
                    min="0"
                    step="0.000001"
                    placeholder="ex: 0.1195"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {t(`${A}.exchangeRateHint`)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm mb-2">{t(`${A}.eurValue`)}</label>
                  <div className="form-control-artsy w-full bg-gray-50 flex items-center text-gray-700 font-semibold">
                    {priceEur
                      ? `${Number(priceEur).toLocaleString("fr-FR")} EUR`
                      : <span className="text-gray-400 font-normal">—</span>
                    }
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    = {acquisitionCurrency} {acquisitionPrice || "—"} × {exchangeRate || "—"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {isAuction && (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-4">
              <p className="text-xs uppercase tracking-wider text-gray-500 font-medium" style={{ fontFamily: "Georgia, serif" }}>
                {t(`${A}.auctionDetail`)}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-2">{t(`${A}.auctionHouse`)}</label>
                  <input
                    type="text"
                    name="auction_house"
                    value={auctionHouse}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleInputChange(e);
                      handleInputChange({ target: { name: "acquisitionDetails.auction_house", value } });
                      handleInputChange({ target: { name: "acquisition_source", value } });
                      handleInputChange({ target: { name: "acquisitionDetails.acquisition_source", value } });
                    }}
                    disabled={disabled}
                    className="form-control-artsy w-full"
                    placeholder="ex: Sotheby&apos;s"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2">{t(`${A}.lotNumber`)}</label>
                  <input
                    type="text"
                    name="auction_lot_number"
                    value={auctionLotNumber}
                    onChange={(e) => {
                      handleInputChange(e);
                      handleInputChange({ target: { name: "acquisitionDetails.auction_lot_number", value: e.target.value } });
                      handleInputChange({ target: { name: "acquisitionDetails.lot_number", value: e.target.value } });
                    }}
                    disabled={disabled}
                    className="form-control-artsy w-full"
                    placeholder="ex: 276"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2">{t(`${A}.saleName`)}</label>
                  <input
                    type="text"
                    name="auction_sale_name"
                    value={auctionSaleName}
                    onChange={(e) => {
                      handleInputChange(e);
                      handleInputChange({ target: { name: "acquisitionDetails.auction_sale_name", value: e.target.value } });
                    }}
                    disabled={disabled}
                    className="form-control-artsy w-full"
                    placeholder="ex: Contemporary Art Day Sale"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2">{t(`${A}.saleDate`)}</label>
                  <input
                    type="date"
                    name="auction_sale_date"
                    value={normalizeDateForInput(auctionSaleDate)}
                    onChange={(e) => {
                      handleInputChange(e);
                      handleInputChange({ target: { name: "acquisitionDetails.auction_sale_date", value: e.target.value } });
                    }}
                    disabled={disabled}
                    className="form-control-artsy w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2">{t(`${A}.hammerPrice`)}</label>
                  <input
                    type="number"
                    name="auction_hammer_price"
                    value={auctionHammerPrice || ""}
                    onChange={(e) => {
                      handleInputChange(e);
                      handleInputChange({ target: { name: "acquisitionDetails.auction_hammer_price", value: e.target.value } });
                      handleInputChange({ target: { name: "acquisitionDetails.hammer_price", value: e.target.value } });
                      syncTotalFromAuction(e.target.value, auctionFees);
                    }}
                    disabled={disabled}
                    className="form-control-artsy w-full"
                    min="0"
                    placeholder="ex: 3500"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2">{t(`${A}.fees`)}</label>
                  <input
                    type="number"
                    name="auction_fees"
                    value={auctionFees || ""}
                    onChange={(e) => {
                      handleInputChange(e);
                      handleInputChange({ target: { name: "acquisitionDetails.auction_fees", value: e.target.value } });
                      syncTotalFromAuction(auctionHammerPrice, e.target.value);
                    }}
                    disabled={disabled}
                    className="form-control-artsy w-full"
                    min="0"
                    placeholder="ex: 945"
                  />
                </div>
              </div>

              {(toNumber(auctionHammerPrice) > 0 || toNumber(auctionFees) > 0) && (
                <div className="flex justify-between items-center text-sm pt-1 border-t border-gray-200">
                  <span className="text-gray-500" style={{ fontFamily: "Georgia, serif" }}>{t(`${A}.totalCalculated`)}</span>
                  <span className="font-semibold text-gray-800">
                    {((toNumber(auctionHammerPrice) || 0) + (toNumber(auctionFees) || 0)).toLocaleString("fr-FR", {
                      style: "currency",
                      currency: acquisitionCurrency || "EUR",
                    })}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AcquisitionSection;